import React, { useState, useEffect } from 'react';
import { Box, Container, Paper } from '@mui/material';
import WelcomeScreen from './WelcomeScreen';
import StepHouseInfo from './StepHouseInfo';
import StepFinishingLevel from './StepFinishingLevel';
import StepReview from './StepReview';
import ResultsSummary from './ResultsSummary';
import SavedProjects from './SavedProjects';
import CompleteHomeEstimator from './CompleteHomeEstimator';
import CompleteHomeResults from './CompleteHomeResults';
import { calculateEstimation, saveProject, getProjects } from '../../utils/estimationEngine';
import { calculateCompleteHome } from '../../utils/completeHomeEngine';
import { generateEstimationPDF } from '../../utils/pdfExport';
import { useGate } from '../../hooks/useGate';
import { GATE_ACTIONS } from '../../constants/gateConfig';
import UsageAlertBanner from '../gate/UsageAlertBanner';

// Screens/Steps
const SCREENS = {
  WELCOME: 'welcome',
  SAVED_PROJECTS: 'saved_projects',
  STEP_HOUSE_INFO: 'step_house_info',
  STEP_FINISHING: 'step_finishing',
  STEP_REVIEW: 'step_review',
  RESULTS: 'results',
  COMPLETE_HOME: 'complete_home',
  COMPLETE_HOME_RESULTS: 'complete_home_results'
};

const Estimator = ({ language = 'en', darkMode = false }) => {
  const [currentScreen, setCurrentScreen] = useState(SCREENS.WELCOME);
  const [formData, setFormData] = useState({
    houseType: '',
    builtUpArea: '',
    floors: null,
    finishingLevel: '',
    projectName: ''
  });
  const [results, setResults] = useState(null);
  const [isCalculating, setIsCalculating] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [savedProjects, setSavedProjects] = useState([]);
  const [compareMode, setCompareMode] = useState(false);
  const [compareResults, setCompareResults] = useState(null);
  const [completeHomeResults, setCompleteHomeResults] = useState(null);
  const [isCompleteHomeSaved, setIsCompleteHomeSaved] = useState(false);

  const { checkAccess, GateModalComponent } = useGate();

  // Load saved projects on mount
  useEffect(() => {
    const projects = getProjects();
    setSavedProjects(projects);
  }, []);

  // Check if there are saved projects
  const hasSavedProjects = savedProjects.length > 0;

  // Handle starting a new estimation
  const handleStartNew = () => {
    setFormData({
      houseType: '',
      builtUpArea: '',
      floors: null,
      finishingLevel: '',
      projectName: ''
    });
    setResults(null);
    setIsSaved(false);
    setCompareMode(false);
    setCompareResults(null);
    setCurrentScreen(SCREENS.STEP_HOUSE_INFO);
  };

  // Handle loading saved projects screen
  const handleLoadProject = () => {
    setSavedProjects(getProjects());
    setCurrentScreen(SCREENS.SAVED_PROJECTS);
  };

  // Handle selecting a saved project
  const handleSelectProject = (project) => {
    setFormData({
      houseType: project.inputs.houseType,
      builtUpArea: project.inputs.builtUpArea,
      floors: project.inputs.floors,
      finishingLevel: project.inputs.finishingLevel,
      projectName: project.inputs.projectName || ''
    });
    setResults(project);
    setIsSaved(true);
    setCurrentScreen(SCREENS.RESULTS);
  };

  // Handle deleting a project
  const handleDeleteProject = (projectId) => {
    const updated = savedProjects.filter(p => p.id !== projectId);
    localStorage.setItem('rumahkira_projects', JSON.stringify(updated));
    setSavedProjects(updated);
  };

  // Handle form data changes
  const handleFormChange = (newData) => {
    setFormData(newData);
  };

  // Handle navigation
  const goToScreen = (screen) => {
    setCurrentScreen(screen);
  };

  // Handle calculation
  const handleCalculate = async () => {
    if (!checkAccess(GATE_ACTIONS.QUICK_ESTIMATE)) return;

    setIsCalculating(true);
    await new Promise(resolve => setTimeout(resolve, 800));

    try {
      const estimationResults = calculateEstimation({
        houseType: formData.houseType,
        builtUpArea: formData.builtUpArea,
        floors: formData.floors,
        finishingLevel: formData.finishingLevel
      });

      estimationResults.inputs.projectName = formData.projectName;

      if (compareMode && results) {
        setCompareResults(estimationResults);
      } else {
        setResults(estimationResults);
      }

      setIsSaved(false);
      setCurrentScreen(SCREENS.RESULTS);
    } catch (error) {
      console.error('Calculation error:', error);
    } finally {
      setIsCalculating(false);
    }
  };

  // Handle saving project
  const handleSave = () => {
    if (!checkAccess(GATE_ACTIONS.SAVE_PROJECT)) return;
    if (results) {
      const savedProject = saveProject(results);
      setResults(savedProject);
      setIsSaved(true);
      setSavedProjects(getProjects());
    }
  };

  // Handle PDF export
  const handleExportPDF = () => {
    if (!checkAccess(GATE_ACTIONS.EXPORT_PDF)) return;
    if (results) {
      generateEstimationPDF(results, language);
    }
  };

  // Handle compare mode
  const handleCompare = () => {
    if (!checkAccess(GATE_ACTIONS.COMPARE_SCENARIOS)) return;
    setCompareMode(true);
    setFormData({
      houseType: '',
      builtUpArea: '',
      floors: null,
      finishingLevel: '',
      projectName: ''
    });
    setCurrentScreen(SCREENS.STEP_HOUSE_INFO);
  };

  // Handle Complete Home Estimator
  const handleStartCompleteHome = () => {
    setCompleteHomeResults(null);
    setIsCompleteHomeSaved(false);
    setCurrentScreen(SCREENS.COMPLETE_HOME);
  };

  // Handle Complete Home calculation
  const handleCompleteHomeCalculate = async (formData) => {
    if (!checkAccess(GATE_ACTIONS.COMPLETE_HOME_ESTIMATE)) return;

    setIsCalculating(true);
    await new Promise(resolve => setTimeout(resolve, 800));

    try {
      const results = calculateCompleteHome(formData);
      setCompleteHomeResults(results);
      setIsCompleteHomeSaved(false);
      setCurrentScreen(SCREENS.COMPLETE_HOME_RESULTS);
    } catch (error) {
      console.error('Complete home calculation error:', error);
    } finally {
      setIsCalculating(false);
    }
  };

  // Handle saving complete home project
  const handleSaveCompleteHome = () => {
    if (!checkAccess(GATE_ACTIONS.SAVE_PROJECT)) return;
    if (completeHomeResults) {
      const projectToSave = {
        ...completeHomeResults,
        id: Date.now().toString(),
        type: 'complete_home',
        savedAt: new Date().toISOString()
      };
      const existingProjects = JSON.parse(localStorage.getItem('rumahkira_projects') || '[]');
      existingProjects.unshift(projectToSave);
      localStorage.setItem('rumahkira_projects', JSON.stringify(existingProjects));
      setIsCompleteHomeSaved(true);
      setSavedProjects([projectToSave, ...savedProjects]);
    }
  };

  // Handle exporting complete home PDF
  const handleExportCompleteHomePDF = () => {
    if (!checkAccess(GATE_ACTIONS.EXPORT_PDF)) return;
    if (completeHomeResults) {
      // Use existing PDF export with adapted data
      const adaptedResults = {
        ...completeHomeResults,
        inputs: {
          ...completeHomeResults.inputs,
          houseType: completeHomeResults.inputs.houseType,
          builtUpArea: completeHomeResults.params.builtUpArea,
          floors: completeHomeResults.params.floors,
          finishingLevel: completeHomeResults.inputs.finishingLevel,
          projectName: completeHomeResults.inputs.projectName
        },
        breakdown: {
          total: completeHomeResults.summary.totalCost,
          structure: { amount: completeHomeResults.summary.categories.find(c => c.id === 'structure')?.amount || 0 },
          finishes: { amount: completeHomeResults.summary.categories.find(c => c.id === 'walls')?.amount || 0 }
        },
        materials: {
          cement: { quantity: completeHomeResults.materials.concrete.cement.avg },
          bricks: { quantity: completeHomeResults.materials.bricks.bricks?.avg || 0 },
          steel: { quantity: completeHomeResults.materials.steel.reinforcement?.avg || 0 },
          sand: { quantity: (completeHomeResults.materials.aggregates.sandFine?.avg || 0) + (completeHomeResults.materials.aggregates.sandCoarse?.avg || 0) },
          gravel: { quantity: completeHomeResults.materials.aggregates.stone?.avg || 0 }
        }
      };
      generateEstimationPDF(adaptedResults, language);
    }
  };

  // Render current screen
  const renderScreen = () => {
    switch (currentScreen) {
      case SCREENS.WELCOME:
        return (
          <WelcomeScreen
            onStartNew={handleStartNew}
            onStartCompleteHome={handleStartCompleteHome}
            onLoadProject={handleLoadProject}
            hasSavedProjects={hasSavedProjects}
            language={language}
          />
        );

      case SCREENS.SAVED_PROJECTS:
        return (
          <SavedProjects
            projects={savedProjects}
            onSelect={handleSelectProject}
            onDelete={handleDeleteProject}
            onBack={() => goToScreen(SCREENS.WELCOME)}
            onStartNew={handleStartNew}
            language={language}
          />
        );

      case SCREENS.STEP_HOUSE_INFO:
        return (
          <StepHouseInfo
            data={formData}
            onChange={handleFormChange}
            onNext={() => goToScreen(SCREENS.STEP_FINISHING)}
            onBack={() => goToScreen(compareMode ? SCREENS.RESULTS : SCREENS.WELCOME)}
            language={language}
          />
        );

      case SCREENS.STEP_FINISHING:
        return (
          <StepFinishingLevel
            data={formData}
            onChange={handleFormChange}
            onNext={() => goToScreen(SCREENS.STEP_REVIEW)}
            onBack={() => goToScreen(SCREENS.STEP_HOUSE_INFO)}
            language={language}
          />
        );

      case SCREENS.STEP_REVIEW:
        return (
          <StepReview
            data={formData}
            onChange={handleFormChange}
            onCalculate={handleCalculate}
            onBack={() => goToScreen(SCREENS.STEP_FINISHING)}
            isCalculating={isCalculating}
            language={language}
          />
        );

      case SCREENS.RESULTS:
        return (
          <ResultsSummary
            results={results}
            compareResults={compareResults}
            onSave={handleSave}
            onExportPDF={handleExportPDF}
            onCompare={handleCompare}
            onStartNew={handleStartNew}
            isSaved={isSaved}
            language={language}
          />
        );

      case SCREENS.COMPLETE_HOME:
        return (
          <CompleteHomeEstimator
            onBack={() => goToScreen(SCREENS.WELCOME)}
            onCalculate={handleCompleteHomeCalculate}
            isCalculating={isCalculating}
            language={language}
          />
        );

      case SCREENS.COMPLETE_HOME_RESULTS:
        return (
          <CompleteHomeResults
            results={completeHomeResults}
            onSave={handleSaveCompleteHome}
            onExportPDF={handleExportCompleteHomePDF}
            onStartNew={handleStartCompleteHome}
            onBack={() => goToScreen(SCREENS.COMPLETE_HOME)}
            isSaved={isCompleteHomeSaved}
            language={language}
          />
        );

      default:
        return (
          <WelcomeScreen
            onStartNew={handleStartNew}
            onLoadProject={handleLoadProject}
            hasSavedProjects={hasSavedProjects}
            language={language}
          />
        );
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: 'background.default',
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      <Container
        maxWidth="md"
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          py: { xs: 0, sm: 2 }
        }}
      >
        {/* Usage Alerts for Budget Features */}
        <Box sx={{ px: { xs: 2, sm: 0 }, pt: { xs: 1, sm: 0 }, mb: { xs: 0, sm: 1 } }}>
          <UsageAlertBanner actionCode={GATE_ACTIONS.QUICK_ESTIMATE} language={language} />
          <UsageAlertBanner actionCode={GATE_ACTIONS.COMPLETE_HOME_ESTIMATE} language={language} />
        </Box>

        <Paper
          elevation={0}
          sx={{
            flex: 1,
            borderRadius: { xs: 0, sm: 3 },
            overflow: 'hidden',
            border: { xs: 'none', sm: '1px solid' },
            borderColor: 'divider'
          }}
        >
          {renderScreen()}
        </Paper>
      </Container>

      {/* Gate Modal */}
      <GateModalComponent />
    </Box>
  );
};

export default Estimator;
